/**
 * Who "we" are, in one place.
 *
 * 🔑 INCORPORATED 2026-09-23. The sole proprietorship "Ankh" was replaced by
 * ANKH CLOUD TECH (OPC) PRIVATE LIMITED, at the same premises, and AnkhOS is
 * its brand. Every screen that names the supplier reads it from here, so a
 * change of entity is one edit rather than a search for a word that also
 * appears in prose, in a brand name, and in a domain.
 *
 * ⛔ THIS IS NOT THE INVOICE SUPPLIER. What appears on an invoice is resolved by
 * radbilling from `radaccess_operator_profile` + `_taxinfo` for the issuing
 * entity, because a document raised last month must still show the person who
 * raised it. Changing this file re-labels the WEBSITE and nothing else — the
 * proprietorship's invoice O/2026-27/00001 (2026-09-12) keeps its own supplier.
 *
 * ⚠️ The legal documents in io.ankhos name the entity in their own text and are
 * NOT generated from this file. Both have to move together, and the documents
 * are the ones a lawyer has to see.
 */
export const ENTITY = {
  /** Legal name, exactly as on the Certificate of Incorporation and GST REG-06. */
  name: "Ankh Cloud Tech (OPC) Private Limited",
  /** The product brand. Not a legal person — never put it where a party is named. */
  brand: "AnkhOS",
  /** How it is constituted. */
  form: "one person company limited by shares, incorporated under the Companies Act, 2013",
  /** Corporate Identity Number, Certificate of Incorporation dated 2026-09-23. */
  cin: "U62011MH2026OPC475990",
  pan: "ABGCA8045B",
  /** GST REG-06, liability from 2026-09-24, Regular. */
  gstin: "27ABGCA8045B1ZV",
  incorporatedOn: "2026-09-23",
  /** Registered office — unchanged by incorporation, same premises. */
  address: [
    "2nd Floor, Khosla Niwas",
    "57, Subhash Road",
    "Vile Parle East",
    "Mumbai, Maharashtra 400057",
  ],
  /** Place of supply for anything we issue, and the courts named in the Terms. */
  state: "Maharashtra",
  jurisdiction: "Mumbai",
  /**
   * TRUE from 2026-09-24 (GSTIN 27ABGCA8045B1ZV, Regular).
   *
   * 🔑 Flipping this boolean rewrites the website's claim. It does NOT rewrite
   * the legal pack: `io.ankhos/legal-final-draft/01-TERMS-OF-SERVICE.md` states
   * it in its own prose. So a test asserts the two AGREE — the failure mode
   * worth defending against is not "we forgot", it is "we changed one of them".
   *
   * ⛔ This does not make an invoice charge GST either. `applyTaxRegime()`
   * forces zero for any non-REGULAR supplier regardless of what any component
   * says, and the component's own `gst_percent` is a second, independent
   * switch. Three things must move; this is the one that changes what we SAY.
   */
  gstRegistered: true,
} as const;

/**
 * The GST sentence, derived rather than written out at each call site.
 *
 * Returns `null` once registered, because at that point the page must not carry
 * a replacement sentence generated here — see `gstRegisteredNotice`.
 */
export function gstNotRegisteredNotice(): string | null {
  if (ENTITY.gstRegistered) return null;
  return `${ENTITY.name} is not currently registered under GST, so what you receive is an Invoice, not a Tax Invoice, carrying the prescribed not-registered declaration. You cannot claim input tax credit on it.`;
}

/**
 * The registered-side sentence. It names the GSTIN, because a Tax Invoice claim
 * is a statement about a real registration number and belongs with the number.
 */
export function gstRegisteredNotice(): string | null {
  if (!ENTITY.gstRegistered) return null;
  return `Prices exclude GST. GST at 18% (SAC 998315) is added to each invoice, and you receive a Tax Invoice from ${ENTITY.name}, GSTIN ${ENTITY.gstin}, on which a GST-registered business can claim input tax credit.`;
}

/** "Ankh Cloud Tech (OPC) Private Limited, 2nd Floor, Khosla Niwas, …" for a single line. */
export function entityAddressLine(): string {
  return [ENTITY.name, ...ENTITY.address].join(", ");
}

/** "CIN … · GSTIN …" — the statutory identifiers a company prints on its site. */
export function entityIdentifiersLine(): string {
  return `CIN ${ENTITY.cin} · GSTIN ${ENTITY.gstin}`;
}
